import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { ContentCard } from '../components/ContentCard';
import { IdCardIcon, MessageSquareIcon, LockIcon, BanIcon, MapPinIcon, AlertTriangleIcon } from 'lucide-react';
const rules = [{
  icon: IdCardIcon,
  title: 'Valid CPL Required',
  description: 'You must have a valid Michigan Concealed Pistol License (CPL) to carry a concealed firearm on your motorcycle.',
  variant: 'important' as const
}, {
  icon: MessageSquareIcon,
  title: 'Immediate Disclosure Required',
  description: 'Michigan law requires you to IMMEDIATELY inform any police officer that you are carrying a concealed weapon during any contact, including traffic stops.',
  variant: 'warning' as const
}, {
  icon: LockIcon,
  title: 'Concealed or Locked',
  description: 'Your firearm must be either concealed on your person or stored in a locked container. A saddlebag with a lock qualifies as a locked container.',
  variant: 'default' as const
}, {
  icon: BanIcon,
  title: 'No Carrying While Impaired',
  description: 'It is illegal to carry a firearm while under the influence of alcohol or drugs. This includes any amount that impairs your ability.',
  variant: 'warning' as const
}, {
  icon: MapPinIcon,
  title: 'Restricted Zones',
  description: 'CPL holders cannot carry in certain locations: schools, daycare centers, sports arenas, bars/taverns, places of worship, hospitals, and more.',
  variant: 'default' as const
}];
const container = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0
  }
};
export function GunLaws() {
  return <div className="min-h-screen bg-[#0a0a0a] safe-bottom" data-id="element-165">
      <PageHeader title="MI Gun Laws" subtitle="Carrying firearms on motorcycles" data-id="element-166" />

      <main className="px-4 py-6" data-id="element-167">
        {/* Critical Warning */}
        <motion.div className="bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-2xl p-4 mb-6 flex gap-3" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} data-id="element-168">
          <AlertTriangleIcon className="w-6 h-6 text-[#dc2626] flex-shrink-0 mt-0.5" data-id="element-169" />
          <div data-id="element-170">
            <p className="text-[#dc2626] font-semibold mb-1" data-id="element-171">
              Critical: Disclosure Law
            </p>
            <p className="text-[#a3a3a3] text-sm" data-id="element-172">
              Failure to immediately disclose that you're carrying can result in
              a civil infraction and CPL suspension.
            </p>
          </div>
        </motion.div>

        <motion.div className="space-y-3" variants={container} initial="hidden" animate="show" data-id="element-173">
          {rules.map((rule, index) => <motion.div key={index} variants={item} data-id="element-174">
              <ContentCard icon={rule.icon} title={rule.title} description={rule.description} variant={rule.variant} data-id="element-175" />
            </motion.div>)}
        </motion.div>

        {/* Restricted Zones List */}
        <motion.div className="mt-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} data-id="element-176">
          <p className="text-white font-semibold mb-3" data-id="element-177">
            Pistol-Free Zones in Michigan:
          </p>
          <ul className="space-y-2 text-sm text-[#a3a3a3]" data-id="element-178">
            <li data-id="element-179">• Schools and school property</li>
            <li data-id="element-180">• Daycare centers</li>
            <li data-id="element-181">• Sports arenas and stadiums</li>
            <li data-id="element-182">• Bars and taverns</li>
            <li data-id="element-183">• Churches and places of worship</li>
            <li data-id="element-184">• Hospitals</li>
            <li data-id="element-185">• Dormitories</li>
            <li data-id="element-186">• Casinos</li>
            <li data-id="element-187">• Entertainment venues (1,000+ capacity)</li>
          </ul>
        </motion.div>
      </main>
    </div>;
}